// src/domains/tags/hooks/useTagsUnified.ts
import { useMemo, useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useEntityList } from '@/hooks/useEntityList';
import { supabaseWorker } from '@/integrations/supabase/workerClient';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Tag, TagCreate, TagUpdate, TagWithChildren, TagFilters, DbTag } from '@/types/entities/tag';
import { dbTagToTag, tagCreateToDbTagInsert, tagUpdateToDbTagUpdate } from '@/utils/mappers/tagMappers';
import { TABLE_NAMES } from '@/constants/tableNames';

// Import domain status hooks
import { useOrderStatuses } from '@/hooks/domain-statuses/useOrderStatuses';
import { useRentalStatuses } from '@/hooks/domain-statuses/useRentalStatuses';
import { usePaymentMethods } from '@/hooks/domain-statuses/usePaymentMethods';
import { usePaymentStatuses } from '@/hooks/domain-statuses/usePaymentStatuses';
import { useInventoryStatuses } from '@/hooks/domain-statuses/useInventoryStatuses';
import { useInventoryConditions } from '@/hooks/domain-statuses/useInventoryConditions';
import { DomainStatus, PaymentMethod } from '@/types/domain-statuses';

const TABLE_NAME = TABLE_NAMES.tags;
const QUERY_KEY = [TABLE_NAME];

// Helper functions to convert domain statuses to Tag format
const domainStatusToTag = (status: DomainStatus, type: Tag['type'], parentId?: string): Tag => {
  const color = 'color' in status && typeof (status as { color?: string }).color === 'string'
    ? (status as { color?: string }).color || '#6b7280'
    : '#6b7280';
  return {
    id: status.id,
    name: status.name,
    type,
    parentId: parentId || null,
    userId: status.userId,
    createdAt: status.createdAt,
    updatedAt: status.updatedAt,
    color,
    description: status.description || null,
    order: 0,
  };
};

const paymentMethodToTag = (method: PaymentMethod, parentId?: string): Tag => ({
  id: method.id,
  name: method.name,
  type: 'order' as Tag['type'],
  parentId: parentId || null,
  userId: method.userId,
  createdAt: method.createdAt,
  updatedAt: method.updatedAt,
  color: '#6b7280', // Payment methods don't have colors
  description: method.description || null,
  order: 0, // Default order for payment methods
});

type UseTagsUnifiedReturn = {
  tags: Tag[];
  tagsWithHierarchy: TagWithChildren[];
  loading: boolean;
  error: unknown;
  refetch: () => void;
  createTag: (tagData: TagCreate) => Promise<Tag | undefined>;
  updateTag: (id: string, updates: TagUpdate) => Promise<Tag | undefined>;
  deleteTag: (id: string) => Promise<boolean>;
  moveTag: (tagId: string, newParentId: string | null) => Promise<Tag | undefined>;
};

export function useTagsUnified(filters?: TagFilters): UseTagsUnifiedReturn {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [isMutating, setIsMutating] = useState(false);

  // Use domain status hooks for specific types
  const { 
    orderStatuses, 
    isLoading: isOrderStatusesLoading,
    error: orderStatusesError,
    refetch: refetchOrderStatuses 
  } = useOrderStatuses();

  const { 
    rentalStatuses, 
    isLoading: isRentalStatusesLoading,
    error: rentalStatusesError,
    refetch: refetchRentalStatuses 
  } = useRentalStatuses();

  const { 
    paymentMethods, 
    isLoading: isPaymentMethodsLoading,
    error: paymentMethodsError,
    refetch: refetchPaymentMethods 
  } = usePaymentMethods();

  const { 
    paymentStatuses, 
    isLoading: isPaymentStatusesLoading,
    error: paymentStatusesError,
    refetch: refetchPaymentStatuses 
  } = usePaymentStatuses();

  const { 
    inventoryStatuses, 
    isLoading: isInventoryStatusesLoading,
    error: inventoryStatusesError,
    refetch: refetchInventoryStatuses 
  } = useInventoryStatuses();

  const { 
    inventoryConditions, 
    isLoading: isInventoryConditionsLoading,
    error: inventoryConditionsError,
    refetch: refetchInventoryConditions 
  } = useInventoryConditions();

  // Use legacy tags system for other types
  const { 
    data: legacyTags, 
    isLoading: isLegacyLoading, 
    error: legacyError, 
    refetch: refetchLegacy 
  } = useEntityList<Tag, typeof TABLE_NAME>({
    tableName: TABLE_NAME,
    queryKey: [...QUERY_KEY, filters?.type],
    dbEntityToEntityMapper: dbTagToTag,
    filters: {
      type: filters?.type && filters.type !== 'all' ? filters.type : undefined,
      parent_id: filters?.parentId,
    },
    enabled: !['order', 'rental', 'inventory'].includes(filters?.type || ''),
  });

  // Combine data from domain hooks and legacy system
  const tags = useMemo(() => {
    const combinedTags: Tag[] = [];

    // Handle specific filter types
    if (filters?.type) {
      switch (filters.type) {
        case 'order':
          // For order type, we need to handle both statuses and payment-related items
          if (filters.parentId) {
            // If parentId is specified, we're looking for child tags (payment methods/statuses)
            if (filters.parentId === 'payment-methods-parent') {
              combinedTags.push(...paymentMethods.map(method => paymentMethodToTag(method, filters.parentId)));
            } else if (filters.parentId === 'payment-statuses-parent') {
              combinedTags.push(...paymentStatuses.map(status => domainStatusToTag(status, 'order', filters.parentId)));
            }
          } else {
            // Root level - only return order statuses, no parent tags for useSettingsTags
            // Parent tags are only needed for hierarchical display in settings UI
            combinedTags.push(...orderStatuses.map(status => domainStatusToTag(status, 'order')));
          }
          break;

        case 'rental':
          combinedTags.push(...rentalStatuses.map(status => domainStatusToTag(status, 'rental')));
          break;

        case 'inventory':
          combinedTags.push(...inventoryStatuses.map(status => domainStatusToTag(status, 'inventory')));
          combinedTags.push(...inventoryConditions.map(condition => domainStatusToTag(condition, 'inventory')));
          break;

        default:
          // Use legacy tags for other types
          combinedTags.push(...(legacyTags || []));
      }
    } else {
      // No filter - return all domain statuses as tags
      combinedTags.push(...orderStatuses.map(status => domainStatusToTag(status, 'order')));
      combinedTags.push(...rentalStatuses.map(status => domainStatusToTag(status, 'rental')));
      combinedTags.push(...paymentMethods.map(method => paymentMethodToTag(method)));
      combinedTags.push(...paymentStatuses.map(status => domainStatusToTag(status, 'order')));
      combinedTags.push(...inventoryStatuses.map(status => domainStatusToTag(status, 'inventory')));
      combinedTags.push(...inventoryConditions.map(condition => domainStatusToTag(condition, 'inventory')));
      combinedTags.push(...(legacyTags || []));
    }

    return combinedTags;
  }, [
    filters?.type, 
    filters?.parentId,
    orderStatuses, 
    rentalStatuses, 
    paymentMethods, 
    paymentStatuses, 
    inventoryStatuses, 
    inventoryConditions, 
    legacyTags
  ]);

  // Aggregate loading states
  const isListLoading = isOrderStatusesLoading || isRentalStatusesLoading || 
    isPaymentMethodsLoading || isPaymentStatusesLoading || 
    isInventoryStatusesLoading || isInventoryConditionsLoading || isLegacyLoading;

  // Aggregate errors
  const error = orderStatusesError || rentalStatusesError || paymentMethodsError || 
    paymentStatusesError || inventoryStatusesError || inventoryConditionsError || legacyError;

  // Aggregate refetch functions
  const refetch = useCallback((): void => {
    refetchOrderStatuses();
    refetchRentalStatuses();
    refetchPaymentMethods();
    refetchPaymentStatuses();
    refetchInventoryStatuses();
    refetchInventoryConditions();
    refetchLegacy();
  }, [
    refetchOrderStatuses, refetchRentalStatuses, refetchPaymentMethods,
    refetchPaymentStatuses, refetchInventoryStatuses, refetchInventoryConditions, refetchLegacy
  ]);

  const invalidateQueries = useCallback((specificType?: string) => {
    if (specificType) {
      // Инвалидируем только кэш для конкретного типа
      queryClient.invalidateQueries({ queryKey: [...QUERY_KEY, specificType] });
    } else {
      // Инвалидируем все кэши тегов
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    }
  }, [queryClient]);

  const createTag = useCallback(async (tagData: TagCreate) => {
    if (!user?.id) {
      toast.error('User not authenticated');
      return;
    }

    setIsMutating(true);
    try {
      const normalizedParentId = tagData.parentId === 'null' ? null : tagData.parentId ?? null;

      let orderQuery = supabaseWorker
        .from('tags')
        .select('order')
        .eq('user_id', user.id)
        .eq('type', tagData.type)
        .order('order', { ascending: false })
        .limit(1);

      orderQuery = normalizedParentId === null
        ? orderQuery.is('parent_id', null)
        : orderQuery.eq('parent_id', normalizedParentId);

      const { data: maxOrderRow, error: maxOrderError } = await orderQuery.maybeSingle();

      if (maxOrderError && maxOrderError.code !== 'PGRST116') {
        throw maxOrderError;
      }

      const nextOrder = typeof maxOrderRow?.order === 'number' ? maxOrderRow.order + 1 : 0;

      const dbData = tagCreateToDbTagInsert(tagData, user.id, nextOrder);
      
      const { data: result, error } = await supabaseWorker
        .from('tags')
        .insert(dbData)
        .select()
        .single();

      if (error) throw error;
      
      // Инвалидируем ВСЕ кэши тегов для корректного отображения
      invalidateQueries(); // Без параметра - инвалидирует все теги
      toast.success('Тег успешно создан');
      return dbTagToTag(result as DbTag);
    } catch (err: unknown) {
      console.error('Error creating tag:', err);
      const message = (err as { message?: string } | null)?.message ?? 'Не удалось создать тег';
      toast.error(message);
      throw err;
    } finally {
      setIsMutating(false);
    }
  }, [user?.id, invalidateQueries]);

  const updateTag = useCallback(async (id: string, updates: TagUpdate) => {
    if (!user?.id) {
      toast.error('User not authenticated');
      return;
    }

    setIsMutating(true);
    try {
      const dbUpdates = tagUpdateToDbTagUpdate(updates);
      
      const { data: result, error } = await supabaseWorker
        .from('tags')
        .update(dbUpdates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      
      // Инвалидируем ВСЕ кэши тегов для корректного отображения
      invalidateQueries(); // Без параметра - инвалидирует все теги
      toast.success('Тег успешно обновлен');
      return dbTagToTag(result as DbTag);
    } catch (err: unknown) {
      console.error('Error updating tag:', err);
      const message = (err as { message?: string } | null)?.message ?? 'Не удалось обновить тег';
      toast.error(message);
      throw err;
    } finally {
      setIsMutating(false);
    }
  }, [user?.id, invalidateQueries]);

  const deleteTag = useCallback(async (id: string) => {
    if (!user?.id) {
      console.error('[deleteTag] User not authenticated');
      toast.error('User not authenticated');
      return false;
    }

    console.log(`[deleteTag] Starting deletion for tag ID: ${id}, user: ${user.id}`);

    setIsMutating(true);
    try {
      // Сначала получаем информацию о теге для поиска дубликатов
      const { data: targetTag, error: tagError } = await supabaseWorker
        .from('tags')
        .select('id, name, type, parent_id')
        .eq('id', id)
        .eq('user_id', user.id)
        .single();
      
      if (tagError || !targetTag) {
        console.error('[deleteTag] Error fetching target tag:', tagError);
        throw tagError || new Error('Tag not found');
      }
      
      const target = targetTag as { id: string; name: string; type: Tag['type']; parent_id: string | null };
      console.log(`[deleteTag] Target tag:`, target);
      
      // Проверяем наличие дочерних тегов у основного тега
      console.log(`[deleteTag] Checking for children of tag ${id}`);
      const { data: children, error: childrenError } = await supabaseWorker
        .from('tags')
        .select('id')
        .eq('parent_id', id)
        .eq('user_id', user.id);
      
      if (childrenError) {
        console.error('[deleteTag] Error checking children:', childrenError);
        throw childrenError;
      }
      
      const childrenList = (children as { id: string }[] | null) || [];
      console.log(`[deleteTag] Found ${childrenList.length} children for tag ${id}`);
      
      if (childrenList.length > 0) {
        console.warn(`[deleteTag] Cannot delete tag ${id} - has ${childrenList.length} children`);
        toast.error('Нельзя удалить тег, у которого есть дочерние теги');
        return false;
      }
      
      // Найти все дубликаты (теги с тем же name, type, parent_id)
      const { data: duplicates, error: duplicatesError } = await supabaseWorker
        .from('tags')
        .select('id, name')
        .eq('user_id', user.id)
        .eq('name', target.name)
        .eq('type', target.type)
        .eq('parent_id', target.parent_id);
      
      if (duplicatesError) {
        console.error('[deleteTag] Error finding duplicates:', duplicatesError);
        throw duplicatesError;
      }
      
      const duplicateIds = ((duplicates as { id: string; name: string }[] | null) || []).map(d => d.id);
      console.log(`[deleteTag] Found ${duplicateIds.length} total tags (including original) with same name/type/parent`);
      
      if (duplicateIds.length > 1) {
        console.log(`[deleteTag] Deleting ${duplicateIds.length} duplicate tags:`, duplicateIds);
        // Удаляем дубликаты по одному (совместимость типов клиента)
        for (const dupId of duplicateIds) {
          const { error: deleteError } = await supabaseWorker
            .from('tags')
            .delete()
            .eq('id', dupId)
            .eq('user_id', user.id);
          if (deleteError) {
            console.error('[deleteTag] Delete error:', deleteError);
            throw deleteError;
          }
        }
        
        console.log(`[deleteTag] Successfully deleted ${duplicateIds.length} duplicate tags`);
        toast.success(`Удален тег "${target.name}" и ${duplicateIds.length - 1} дубликат(ов)`);
      } else {
        // Удаляем только один тег
        const { error: deleteError } = await supabaseWorker
          .from('tags')
          .delete()
          .eq('id', id)
          .eq('user_id', user.id);
        
        if (deleteError) {
          console.error('[deleteTag] Delete error:', deleteError);
          throw deleteError;
        }
        
        console.log(`[deleteTag] Successfully deleted tag ${id}`);
        toast.success(`Тег "${target.name}" успешно удален`);
      }
      
      // Инвалидируем ВСЕ кэши тегов
      invalidateQueries();
      return true;
    } catch (err: unknown) {
      console.error(`[deleteTag] Error deleting tag ${id}:`, err);
      const e = err as { message?: string; code?: string; details?: unknown; hint?: unknown } | null;
      console.error(`[deleteTag] Error details:`, {
        message: e?.message,
        code: e?.code,
        details: e?.details,
        hint: e?.hint
      });
      toast.error(`Не удалось удалить тег: ${e?.message || 'Неизвестная ошибка'}`);
      return false;
    } finally {
      setIsMutating(false);
    }
  }, [user?.id, invalidateQueries]);

  const moveTag = useCallback(async (tagId: string, newParentId: string | null) => {
    if (!user?.id) {
      toast.error('User not authenticated');
      return;
    }

    setIsMutating(true);
    try {
      const { data: result, error } = await supabaseWorker
        .from('tags')
        .update({ parent_id: newParentId })
        .eq('id', tagId)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;
      
      // Инвалидируем только кэш для типа 'order' (где находятся теги оплаты)
      invalidateQueries('order');
      return dbTagToTag(result as DbTag);
    } catch (err: unknown) {
      console.error('Error moving tag:', err);
      const message = (err as { message?: string } | null)?.message ?? 'Не удалось переместить тег';
      toast.error(message);
      throw err;
    } finally {
      setIsMutating(false);
    }
  }, [user?.id, invalidateQueries]);

  const tagsWithHierarchy = useMemo(() => {
    const tagMap: Record<string, TagWithChildren> = {};
    const rootTags: TagWithChildren[] = [];
    
    tags.forEach(tag => {
      tagMap[tag.id] = { ...tag, children: [] };
    });
    
    tags.forEach(tag => {
      if (tag.parentId && tagMap[tag.parentId]) {
        tagMap[tag.parentId].children?.push(tagMap[tag.id]);
      } else {
        rootTags.push(tagMap[tag.id]);
      }
    });
    
    return rootTags;
  }, [tags]);

  return {
    tags: tags || [],
    tagsWithHierarchy,
    loading: isListLoading || isMutating,
    error,
    refetch,
    createTag,
    updateTag,
    deleteTag,
    moveTag
  };
}

// Экспорт типов для использования в компонентах
export type { Tag, TagWithChildren } from '@/types/entities/tag';
